import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLeadCapture } from "@/contexts/LeadCaptureContext";
import { Loader2 } from "lucide-react";

const WEBHOOK_URL = "https://n8n.srv900253.hstgr.cloud/webhook/open-to-work-ai/ai/dados";
const EXTENSION_URL = "https://chromewebstore.google.com/detail/opentoworkai/nplkojeekiipcbhbmdnolhbgkacdcmfk";

interface FormData {
  nomeCompleto: string;
  email: string;
  telefone: string;
}

interface FormErrors {
  nomeCompleto?: string;
  email?: string;
  telefone?: string;
}

const LeadCaptureModal = () => {
  const { isOpen, closeModal } = useLeadCapture();
  const [formData, setFormData] = useState<FormData>({
    nomeCompleto: "",
    email: "",
    telefone: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.nomeCompleto.trim()) {
      newErrors.nomeCompleto = "Nome completo é obrigatório";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email é obrigatório";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Email inválido";
    }

    if (!formData.telefone.trim()) {
      newErrors.telefone = "Telefone é obrigatório";
    } else {
      // Remove caracteres não numéricos para validação
      const phoneDigits = formData.telefone.replace(/\D/g, '');
      if (phoneDigits.length < 10 || phoneDigits.length > 11) {
        newErrors.telefone = "Telefone inválido";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatPhone = (value: string): string => {
    // Remove tudo que não é número
    const numbers = value.replace(/\D/g, '');
    
    // Aplica a máscara (XX) XXXXX-XXXX ou (XX) XXXX-XXXX
    if (numbers.length <= 10) {
      return numbers.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3').replace(/-$/, '');
    } else {
      return numbers.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3').replace(/-$/, '');
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value);
    setFormData({ ...formData, telefone: formatted });
    if (errors.telefone) {
      setErrors({ ...errors, telefone: undefined });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          nomeCompleto: formData.nomeCompleto.trim(),
          email: formData.email.trim(),
          telefone: formData.telefone.trim(),
        }),
      });

      if (!response.ok) {
        throw new Error('Erro ao enviar dados');
      }

      // Fechar modal e redirecionar para a extensão
      closeModal();
      window.open(EXTENSION_URL, '_blank');
      
      // Resetar formulário
      setFormData({
        nomeCompleto: "",
        email: "",
        telefone: "",
      });
      setErrors({});
    } catch (error) {
      console.error('Erro ao enviar dados:', error);
      alert('Erro ao enviar dados. Por favor, tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    closeModal();
    setFormData({
      nomeCompleto: "",
      email: "",
      telefone: "",
    });
    setErrors({});
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-gray-900">
            Acessar Extensão
          </DialogTitle>
          <DialogDescription className="text-base text-gray-600 mt-2">
            Preencha seus dados para continuar e acessar a extensão
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="nomeCompleto" className="text-base font-medium text-gray-900">
              Nome Completo <span className="text-red-500">*</span>
            </Label>
            <Input
              id="nomeCompleto"
              type="text"
              placeholder="Seu nome completo"
              value={formData.nomeCompleto}
              onChange={(e) => {
                setFormData({ ...formData, nomeCompleto: e.target.value });
                if (errors.nomeCompleto) {
                  setErrors({ ...errors, nomeCompleto: undefined });
                }
              }}
              className={errors.nomeCompleto ? "border-red-500" : ""}
              disabled={isSubmitting}
            />
            {errors.nomeCompleto && (
              <p className="text-sm text-red-500">{errors.nomeCompleto}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-base font-medium text-gray-900">
              Email <span className="text-red-500">*</span>
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="seu@email.com"
              value={formData.email}
              onChange={(e) => {
                setFormData({ ...formData, email: e.target.value });
                if (errors.email) {
                  setErrors({ ...errors, email: undefined });
                }
              }}
              className={errors.email ? "border-red-500" : ""}
              disabled={isSubmitting}
            />
            {errors.email && (
              <p className="text-sm text-red-500">{errors.email}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="telefone" className="text-base font-medium text-gray-900">
              Telefone <span className="text-red-500">*</span>
            </Label>
            <Input
              id="telefone"
              type="tel"
              placeholder="(11) 98765-4321"
              value={formData.telefone}
              onChange={handlePhoneChange}
              className={errors.telefone ? "border-red-500" : ""}
              disabled={isSubmitting}
              maxLength={15}
            />
            {errors.telefone && (
              <p className="text-sm text-red-500">{errors.telefone}</p>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0 mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isSubmitting}
              className="w-full sm:w-auto"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full sm:w-auto bg-linkedin hover:bg-linkedin-dark text-white"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Enviando...
                </>
              ) : (
                "Continuar para Extensão"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LeadCaptureModal;

